import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  FormLabel,
  Grid as GridBase,
  Paper,
  Typography,
} from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import moment from "moment";
import type { Moment } from "moment";

import BulkSelectionLayoutBase from "@/features/campaigns/components/bulkSelection/BulkSelectionLayout";
import RpDatePickerBase from "@/components/muiCustom/date/RpDatePicker";
import RpFormErrorBase from "@/components/muiCustom/error/RpFormError";
import RpCurrencyFieldBase from "@/components/muiCustom/textField/RpCurrencyField";
import { RpSweetAlert } from "@/components/alerts/RpSweetAlert";
import { extendCampaignHelper } from "@/features/campaigns/digital/extend-campaign/utils/extendCampaignHelper";
import { CAMPAIGN_CONST, DATE_CONST } from "@/config/constants";
import { shouldBlockUnsafeSubmission } from "@/helpers/sanitizeHelper";
import CreativeUpdateEligibilityDaysBase from "../CreativeUpdateEligibilityDays";
import ExtendHeaderTooltipBase from "../ExtendHeaderTooltip";

type Id = number | string;
type DateValue = string | Date | Moment | null | undefined;

type ExtendEnrollment = {
  start_date?: DateValue;
  end_date?: DateValue;
};

type ExtendLocationData = {
  location_id: Id;
  location_name?: string;
  end_date?: DateValue;
  subscription?: {
    subscription_end_date?: DateValue;
  } | null;
  extend_enrollments?: ExtendEnrollment[] | null;
  [key: string]: unknown;
};

type ExtendCampaignData = {
  evergreen_participant_min_value?: number | string | null;
  evergreen_participant_max_value?: number | string | null;
  overall_minimum_budget_extend?: number;
  daily_maximum_budget_extend?: number;
  daily_minimum_budget_extend?: number;
  availability_window?: string | null;
  availability_window_date?: {
    window_start_date?: DateValue;
    window_end_date?: DateValue;
  } | null;
  location_fees_covered?: boolean | null;
  contract_end_date?: DateValue;
  location_data?: ExtendLocationData[];
};

type BulkFormValues = {
  extend_start_date?: DateValue;
  extend_end_date?: DateValue;
  extend_budget?: number | string | null;
};

type LocationSelectionValue = {
  extend_start_date?: string;
  extend_end_date?: string;
  extend_budget?: number | string | null;
  can_update_creatives?: boolean;
  [key: string]: unknown;
};

type LocationFormikValues = {
  selectedLoc?: Record<Id, LocationSelectionValue>;
};

type ExtendLocationBulkSelectionProps = {
  onToggleBulkSelection?: (_open?: boolean) => void;
  checkedLocations?: Id[];
  campaignData?: ExtendCampaignData;
  setLocationValues: (
    _updater: (_prevValues: LocationFormikValues) => LocationFormikValues,
    _shouldValidate?: boolean
  ) => void;
  setDisableLocations: React.Dispatch<React.SetStateAction<Id[]>>;
};

const BulkSelectionLayout = BulkSelectionLayoutBase as React.ComponentType<
  Record<string, unknown> & { children?: React.ReactNode }
>;
const Grid = GridBase as React.ComponentType<
  Record<string, unknown> & { children?: React.ReactNode }
>;
const RpDatePicker = RpDatePickerBase as React.ComponentType<
  Record<string, unknown>
>;
const RpFormError = RpFormErrorBase as React.ComponentType<
  Record<string, unknown>
>;
const RpCurrencyField = RpCurrencyFieldBase as React.ComponentType<
  Record<string, unknown>
>;
const CreativeUpdateEligibilityDays =
  CreativeUpdateEligibilityDaysBase as React.ComponentType<
    Record<string, unknown>
  >;
const ExtendHeaderTooltip = ExtendHeaderTooltipBase as React.ComponentType<
  Record<string, unknown>
>;

const createValidationSchema = (campaignData?: ExtendCampaignData) => {
  return Yup.object().shape({
    extend_start_date: Yup.date().required("Please select a start date"),
    extend_end_date: Yup.date().required("Please select an end date"),
    extend_budget: Yup.number()
      .test("recommended-budget", function (value, context) {
        const extend_start_date = context.parent.extend_start_date;
        const extend_end_date = context.parent.extend_end_date;

        // If dates are not selected, do nothing
        if (!extend_start_date || !extend_end_date) {
          return true;
        }

        const {
          overall_minimum_budget_extend,
          daily_maximum_budget_extend,
          daily_minimum_budget_extend,
        } = campaignData || {};

        const days = moment(extend_end_date).diff(
          moment(extend_start_date).subtract(1, "day"),
          "days"
        );

        const dailyMinimumBudget = Number(daily_minimum_budget_extend) * days;
        const dailyMaximumBudget = Number(daily_maximum_budget_extend) * days;

        const minPossibleBudget = Math.max(
          dailyMinimumBudget,
          Number(overall_minimum_budget_extend)
        );

        // Show recommendation when budget is empty (triggered when end date is selected)
        if (!value || value.toString().trim() === "") {
          return this.createError({
            path: "extend_budget",
            message: `We recommend a budget between $${minPossibleBudget} and $${dailyMaximumBudget}`,
          });
        }

        // Validate budget value if it exists
        if (value < minPossibleBudget) {
          return this.createError({
            path: "extend_budget",
            message: `We recommend a budget between $${minPossibleBudget} and $${dailyMaximumBudget}`,
          });
        }
        if (value > dailyMaximumBudget) {
          return this.createError({
            path: "extend_budget",
            message: `We recommend a budget between $${minPossibleBudget} and $${dailyMaximumBudget}`,
          });
        }

        return true;
      })
      .required("Please enter a budget"),
  });
};

function ExtendLocationBulkSelection({
  onToggleBulkSelection,
  checkedLocations,
  campaignData,
  setLocationValues,
  setDisableLocations,
}: ExtendLocationBulkSelectionProps) {
  const handleSubmit = async (
    values: BulkFormValues,
    {
      setSubmitting,
      resetForm,
    }: {
      setSubmitting: (_isSubmitting: boolean) => void;
      resetForm: (_nextState?: {
        values?: BulkFormValues;
        errors?: Record<string, unknown>;
        touched?: Record<string, unknown>;
      }) => void;
    }
  ) => {
    try {
      if (shouldBlockUnsafeSubmission(values)) {
        setSubmitting(false);
        return;
      }

      if (!checkedLocations?.length) {
        RpSweetAlert.fire({
          title: "No locations selected",
          text: "Please select at least one location",
          icon: "error",
          confirmButtonText: "OK",
          showCancelButton: false,
        });
        return;
      }

      const extend_start_date =
        values.extend_start_date && moment(values.extend_start_date).isValid()
          ? moment(values.extend_start_date).format("YYYY-MM-DD")
          : "";
      const extend_end_date =
        values.extend_end_date && moment(values.extend_end_date).isValid()
          ? moment(values.extend_end_date).format("YYYY-MM-DD")
          : "";
      const extend_budget = values.extend_budget || 0;

      setLocationValues((prevValues) => {
        const selectedLoc = prevValues.selectedLoc || {};
        const updatedSelectedLoc = { ...selectedLoc };
        checkedLocations.forEach((location) => {
          const locationData = selectedLoc[location] || {};

          // Calculate can_update_creatives for each location when dates are set
          let can_update_creatives = true;
          if (extend_start_date && extend_end_date) {
            can_update_creatives = extendCampaignHelper.findCanUpdateCreative(
              extend_start_date,
              extend_end_date,
              campaignData
            );
          }

          updatedSelectedLoc[location] = {
            ...locationData,
            extend_start_date: extend_start_date,
            extend_end_date: extend_end_date,
            extend_budget: extend_budget,
            can_update_creatives: can_update_creatives,
          };
        });
        return {
          ...prevValues,
          selectedLoc: updatedSelectedLoc,
        };
      }, true);

      RpSweetAlert.fire({
        text: "Bulk selections successful!",
        icon: "success",
        confirmButtonText: "OK",
        showCancelButton: false,
      }).then((result) => {
        if (result.isConfirmed) {
          resetForm({
            values: {
              extend_start_date: null,
              extend_end_date: null,
              extend_budget: "",
            },
            errors: {},
            touched: {},
          });
        }
      });
    } catch (error) {
    } finally {
      setSubmitting(false);
    }
  };

  const formik = useFormik<BulkFormValues>({
    initialValues: {
      extend_start_date: "",
      extend_end_date: "",
      extend_budget: "",
    },
    validateOnChange: true,
    validateOnBlur: false,
    validateOnMount: false,
    onSubmit: handleSubmit,
    enableReinitialize: true,
    validationSchema: createValidationSchema(campaignData),
  });

  const {
    setFieldValue,
    setFieldTouched,
    setValues,
    validateField,
    errors,
    values,
    touched,
    isSubmitting,
  } = formik;

  const [endDatePickerOpen, setEndDatePickerOpen] = useState(false);

  const dateConstraints = useMemo(() => {
    const {
      location_data,
      availability_window,
      location_fees_covered,
      availability_window_date,
      contract_end_date,
    } = campaignData || {};

    const isAvailableWindow =
      availability_window &&
      availability_window !==
        CAMPAIGN_CONST.AVAILABILITY_WINDOW.TYPE.ALWAYS_AVAILABLE;

    const minStartDates = location_data?.map((loc) => {
      const { minStartDate } =
        extendCampaignHelper.getExtendCampaignMinMaxStartDate(
          loc,
          campaignData
        );
      return minStartDate || moment().format("YYYY-MM-DD");
    });

    const maxStartDates = location_data?.map((loc) => {
      const { maxStartDate } =
        extendCampaignHelper.getExtendCampaignMinMaxStartDate(
          loc,
          campaignData
        );
      return maxStartDate;
    });

    const minStartDate = moment.min(
      ...(minStartDates || []).map((date) => moment(date))
    );
    const maxStartDate = moment.max(
      ...(maxStartDates || []).map((date) => moment(date))
    );

    const minEndDate = moment(values.extend_start_date).add(
      CAMPAIGN_CONST.EXTEND.MIN_EXTEND_DAYS - 1,
      "day"
    );

    const maxEndDate = location_data?.reduce<Moment | null>((maxDate, loc) => {
      const { subscription } = loc;
      const { subscription_end_date } = subscription || {};

      const calculatedEndDate = moment.min(
        !location_fees_covered
          ? moment(subscription_end_date)
          : moment(contract_end_date),
        isAvailableWindow
          ? moment(availability_window_date?.window_end_date)
          : moment(DATE_CONST.INFINITY_DATE)
      );

      // If first iteration OR current date is greater → update
      if (!maxDate || calculatedEndDate.isAfter(maxDate)) {
        return calculatedEndDate;
      }

      return maxDate;
    }, null);

    return {
      minStartDate: minStartDate,
      maxStartDate: maxStartDate,
      minEndDate: minEndDate,
      maxEndDate: maxEndDate,
    };
  }, [values.extend_start_date, campaignData]);

  const daySlots = useMemo(() => {
    const { evergreen_participant_min_value, evergreen_participant_max_value } =
      campaignData || {};

    const minEndDate = moment(values.extend_start_date).add(
      CAMPAIGN_CONST.EXTEND.MIN_EXTEND_DAYS - 1,
      "day"
    );

    const minJoinDate = moment(values.extend_start_date).add(
      Number(evergreen_participant_min_value) - 1,
      "day"
    );

    const maxEndDate = moment(values.extend_start_date).add(
      Number(evergreen_participant_max_value) - 1,
      "day"
    );

    const isMinJoinDateBeforeMinEndDate = minJoinDate.isBefore(
      minEndDate,
      "day"
    );

    const canNotUpdateDateMax = minEndDate.isSameOrAfter(minJoinDate, "day")
      ? minEndDate
      : minJoinDate;

    let canNotUpdateDate = [
      minEndDate.format("YYYY-MM-DD"),
      canNotUpdateDateMax.format("YYYY-MM-DD"),
    ];

    if (isMinJoinDateBeforeMinEndDate) {
      canNotUpdateDate = [];
    }

    const canUpdateDateMin = isMinJoinDateBeforeMinEndDate
      ? minEndDate
      : canNotUpdateDateMax.clone().add(1, "days");

    const canUpdateDate = [
      canUpdateDateMin.format("YYYY-MM-DD"),
      maxEndDate.format("YYYY-MM-DD"),
    ];

    return (props: Record<string, unknown>) => (
      <CreativeUpdateEligibilityDays
        {...props}
        canUpdateDate={canUpdateDate}
        canNotUpdateDate={canNotUpdateDate}
        startDate={values.extend_start_date}
      />
    );
  }, [values.extend_start_date, campaignData]);

  useEffect(() => {
    const disableLocations: Id[] = [];
    const locationsList = campaignData?.location_data;

    if (!locationsList?.length) {
      setDisableLocations([]);
      return;
    }

    locationsList?.forEach((loc) => {
      const { minStartDate, maxStartDate } =
        extendCampaignHelper.getExtendCampaignMinMaxStartDate(
          loc,
          campaignData
        );
      const { minEndDate, maxEndDate } =
        extendCampaignHelper.getExtendCampaignMinMaxEndDate(
          values.extend_start_date,
          loc,
          campaignData
        );

      if (
        moment(values.extend_start_date).isBefore(minStartDate, "day") ||
        moment(values.extend_start_date).isAfter(maxStartDate, "day")
      ) {
        disableLocations.push(loc.location_id);
      }
      if (
        moment(values.extend_end_date).isBefore(minEndDate, "day") ||
        moment(values.extend_end_date).isAfter(maxEndDate, "day")
      ) {
        disableLocations.push(loc.location_id);
        return;
      }

      // Check if date range conflicts with existing extended ranges
      if (values.extend_start_date && values.extend_end_date) {
        const startDateStr = moment(values.extend_start_date).isValid()
          ? moment(values.extend_start_date).format("YYYY-MM-DD")
          : null;
        const endDateStr = moment(values.extend_end_date).isValid()
          ? moment(values.extend_end_date).format("YYYY-MM-DD")
          : null;

        if (startDateStr && endDateStr) {
          const conflict =
            extendCampaignHelper.validateDateRangeAgainstExtendedRanges(
              startDateStr,
              endDateStr,
              loc
            );

          if (conflict) {
            disableLocations.push(loc.location_id);
          }
        }
      }
    });

    setDisableLocations([...new Set(disableLocations)]);
  }, [
    values.extend_start_date,
    values.extend_end_date,
    campaignData,
    setDisableLocations,
  ]);

  const handleButtonClick = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();

    // Mark all fields as touched
    await formik.setTouched({
      extend_start_date: true,
      extend_end_date: true,
      extend_budget: true,
    });

    // Validate and submit
    const errors = await formik.validateForm();
    if (Object.keys(errors).length === 0) {
      formik.submitForm();
    }
  };

  const handleOnSelectStartDate = (v: Moment | null) => {
    if (!v) {
      setValues({
        extend_start_date: v,
        extend_end_date: "", // reset end date
      });
      setFieldTouched("extend_start_date", true);
      setFieldTouched("extend_end_date", false);
      setEndDatePickerOpen(false);
      return;
    }

    setValues({
      extend_start_date: v,
      extend_end_date: "", // reset end date
    });
    setFieldTouched("extend_start_date", true);
    setFieldTouched("extend_end_date", false);

    // Automatically open end date picker after start date is selected
    setTimeout(() => {
      setEndDatePickerOpen(true);
    }, 100);
  };

  const handleOnSelectEndDate = (v: Moment | null) => {
    setFieldValue("extend_end_date", v);
    setFieldTouched("extend_end_date", true);

    // Trigger budget validation to show recommendation message when end date is selected
    if (v && values.extend_start_date) {
      setFieldTouched("extend_budget", true);
      validateField("extend_budget");
    }
  };

  const handleOnExtendBudgetChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const v = e.target.value;
    setFieldValue("extend_budget", v);
    setFieldTouched("extend_budget", true);
  };

  return (
    <div>
      <BulkSelectionLayout
        onToggleBulkSelection={onToggleBulkSelection}
        collapsedHeading="Want to extend in bulk"
        expandedHeading="Dates and Budget"
      >
        <Paper className="pb-lg-3" elevation={0} square>
          <Grid container spacing={2} className="p-0 m-0">
            <Grid item size={{ xs: 12, md: "grow" }}>
              <FormLabel className="title fw-sb">
                New Start Date{" "}
                <ExtendHeaderTooltip label="Bulk New Start Date" />
              </FormLabel>
              <RpDatePicker
                value={values?.extend_start_date || null}
                onChange={handleOnSelectStartDate}
                // disabled={!isLocationSelected}
                placeholder="Start Date"
                minDate={dateConstraints.minStartDate}
                maxDate={dateConstraints.maxStartDate}
                smallSize={true}
              />
              <RpFormError
                error={errors?.extend_start_date}
                show={touched?.extend_start_date}
              />
            </Grid>
            <Grid item size={{ xs: 12, md: "grow" }}>
              <FormLabel className="title fw-sb">
                New End Date <ExtendHeaderTooltip label="New End Date" />
              </FormLabel>
              <RpDatePicker
                name="extend_end_date"
                value={values?.extend_end_date || null}
                onChange={handleOnSelectEndDate}
                disabled={!values?.extend_start_date}
                placeholder="End Date"
                minDate={dateConstraints.minEndDate}
                maxDate={dateConstraints.maxEndDate}
                slots={{
                  day: daySlots,
                }}
                smallSize={true}
                open={endDatePickerOpen}
                onOpenChange={setEndDatePickerOpen}
              />
              <RpFormError
                error={errors?.extend_end_date}
                show={touched?.extend_end_date}
              />
            </Grid>
            <Grid item size={{ xs: 12, md: "grow" }}>
              <FormLabel className="title fw-sb">Budget</FormLabel>
              <RpCurrencyField
                name="extend_budget"
                placeholder="$0.00 USD"
                value={values?.extend_budget || ""}
                onChange={handleOnExtendBudgetChange}
                error={Boolean(touched?.extend_budget && errors?.extend_budget)}
                helperText={
                  touched?.extend_budget ? (
                    <RpFormError
                      error={errors?.extend_budget}
                      component="span"
                      greyColor={true}
                    />
                  ) : undefined
                }
                decimalScale={0}
                smallSize={true}
              />
            </Grid>
            <Grid
              item
              size={{ xs: 12, lg: "grow" }}
              sx={{ textAlign: "right" }}
            >
              <Button
                onClick={handleButtonClick}
                variant="contained"
                color="primary"
                disabled={isSubmitting}
                sx={{
                  mt: { xs: 1, lg: 4 },
                  px: { xs: "auto", lg: 1, xl: 2 },
                }}
              >
                Apply to selected locations
              </Button>
            </Grid>
          </Grid>
        </Paper>

        <Typography variant="caption">
          <span className="fw-b">Pro tip:</span> If you want to make minor
          changes to individual locations, first make bulk selections and then
          edit individual locations.
        </Typography>
      </BulkSelectionLayout>
    </div>
  );
}

export default ExtendLocationBulkSelection;
